/**
 * Walk the schema alongside a data pointer to find the subschema for the node
 * at that pointer. This is what lets the UI say "I'm at /pages/0/blocks/0" and
 * get back the Block schema (a oneOf), rather than having to know up front.
 *
 * Walking has to consult BOTH the schema and the data because tagged unions
 * can't be traversed without the data's discriminator value. We also resolve
 * $refs and allOf as we go, so callers don't see unresolved indirection.
 */

import type { JsonSchema, JsonValue } from '@bliksys/shared';

import { UnsupportedSchemaError } from './errors.js';
import { parsePointer, type JsonPointer } from './pointer.js';
import { isConstraintUnion, inferDiscriminator, readConstProperty } from './predicates.js';
import { resolveSchema, type RefRegistry } from './resolver.js';

export interface WalkResult {
  /** Fully resolved schema at the target pointer. */
  schema: JsonSchema;
  /** Data value at the target pointer, if any. */
  value: JsonValue | undefined;
}

/**
 * Walk from the root to the schema that applies at `pointer`.
 * Throws UnsupportedSchemaError if the pointer dives through something we
 * can't interpret (e.g. an untyped node with no properties/items).
 *
 * The optional `registry` enables remote `$ref` resolution; without it,
 * remote refs throw `UnsupportedSchemaError`.
 */
export function schemaAtPointer(
  pointer: JsonPointer,
  rootSchema: JsonSchema,
  rootData: JsonValue,
  registry?: RefRegistry,
): WalkResult {
  const segments = parsePointer(pointer);
  let schema = resolveSchema(rootSchema, rootSchema, undefined, registry);
  let value: JsonValue | undefined = rootData;

  for (const segment of segments) {
    const step = stepInto(schema, value, segment, rootSchema, pointer, registry);
    schema = step.schema;
    value = step.value;
  }

  return { schema, value };
}

function stepInto(
  schema: JsonSchema,
  value: JsonValue | undefined,
  segment: string,
  rootSchema: JsonSchema,
  pointerForErrors: JsonPointer,
  registry?: RefRegistry,
): WalkResult {
  // If the current schema is a oneOf/anyOf, pick the active variant based on
  // the data's discriminator value BEFORE stepping in. Constraint unions
  // (variants that only refine the parent, e.g. alternative `format`s on a
  // string) are a no-op for navigation — the parent's type carries the
  // descent logic.
  const variants = schema.oneOf ?? schema.anyOf;
  if (variants && variants.length > 0 && !isConstraintUnion(variants)) {
    const active = activeVariant(schema, value, rootSchema, registry);
    if (active === null) {
      // No active variant — the caller is descending into an unresolved union.
      // Best we can do is return an "unsupported" shape; let the engine decide.
      throw new UnsupportedSchemaError(
        'Cannot descend into oneOf/anyOf without a matching discriminator',
        pointerForErrors,
      );
    }
    schema = active;
  }

  const type = normalizeType(schema.type, schema);

  if (type === 'object') {
    const childSchema = schema.properties?.[segment];
    if (!childSchema) {
      // additionalProperties — if it's a schema, use it; if true/undefined, treat as unknown.
      const ap = schema.additionalProperties;
      if (typeof ap === 'object') {
        // The user-chosen key (segment) is the natural title for an
        // additionalProperties entry — it's what the user named when
        // they added the entry. Use it as a fallback so a wrapper like
        // `{ $ref: '#/$defs/Schema' }` doesn't surface the meta-schema's
        // own "Schema" title to the user (e.g. for $defs entries).
        return {
          schema: resolveAndKeepAnnotations(ap, rootSchema, registry, segment),
          value: pluckObjectValue(value, segment),
        };
      }
      throw new UnsupportedSchemaError(`No schema for property "${segment}"`, pointerForErrors);
    }
    return {
      schema: resolveAndKeepAnnotations(childSchema, rootSchema, registry),
      value: pluckObjectValue(value, segment),
    };
  }

  if (type === 'array') {
    const itemSchema = schema.items;
    if (!itemSchema) {
      throw new UnsupportedSchemaError('Array has no items schema', pointerForErrors);
    }
    return {
      schema: resolveAndKeepAnnotations(itemSchema, rootSchema, registry),
      value: pluckArrayValue(value, segment),
    };
  }

  throw new UnsupportedSchemaError(
    `Cannot step into non-container type "${String(type)}"`,
    pointerForErrors,
  );
}

/**
 * Resolve a schema reference, but keep wrapper-side display annotations
 * (`title`, `description`, `x-bliksys`) from the original. This matters
 * for property/item slots authored as `{ title: 'If', $ref: '#/$defs/Schema' }`:
 * the resolver follows the ref and returns the target schema's properties,
 * so the wrapper's authored title would be lost without this preservation.
 *
 * The wrapper wins when both sides set the same key — that's the author's
 * intent. The target's other fields (type, properties, oneOf, etc.) come
 * through unchanged because the resolver already returns them.
 */
export function resolveAndKeepAnnotations(
  wrapper: JsonSchema,
  rootSchema: JsonSchema,
  registry: RefRegistry | undefined,
  defaultTitle?: string,
): JsonSchema {
  const resolved = resolveSchema(wrapper, rootSchema, undefined, registry);
  // No annotations to preserve when the wrapper had no $ref (resolved IS
  // the wrapper) OR when the wrapper carried no display annotations.
  // Even in that case, a defaultTitle (the segment name for an
  // additionalProperties entry) MAY still need to be applied — see below.
  const overlay: Partial<JsonSchema> = {};
  if (wrapper !== resolved) {
    if (wrapper.title !== undefined) overlay.title = wrapper.title;
    if (wrapper.description !== undefined) overlay.description = wrapper.description;
    if (wrapper['x-bliksys'] !== undefined) {
      overlay['x-bliksys'] = { ...resolved['x-bliksys'], ...wrapper['x-bliksys'] };
    }
  }
  // defaultTitle (the segment name from additionalProperties) wins over
  // the resolved schema's title BUT loses to the wrapper's own title.
  // Without this fallback, $defs entries authored as
  // `{ $ref: '#/$defs/Schema' }` surface the meta-schema's "Schema"
  // title instead of the user's chosen key (e.g. "Block").
  if (defaultTitle !== undefined && overlay.title === undefined && wrapper.title === undefined) {
    overlay.title = defaultTitle;
  }
  if (Object.keys(overlay).length === 0) return resolved;
  return { ...resolved, ...overlay };
}

function activeVariant(
  schema: JsonSchema,
  value: JsonValue | undefined,
  rootSchema: JsonSchema,
  registry?: RefRegistry,
): JsonSchema | null {
  const variants = schema.oneOf ?? schema.anyOf ?? [];
  if (variants.length === 0) return null;
  const resolvedVariants = variants.map((v) => resolveSchema(v, rootSchema, undefined, registry));
  // Prefer explicit discriminator; otherwise infer — matches build.ts.
  const discProp = schema.discriminator?.propertyName ?? inferDiscriminator(resolvedVariants);
  if (!discProp) {
    // No discriminator — we can't safely pick a variant. If there's exactly one
    // variant, use it; otherwise fall through to shape matching.
    if (resolvedVariants.length === 1) return resolvedVariants[0]!;
    return shapeMatchVariant(resolvedVariants, value);
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return shapeMatchVariant(resolvedVariants, value);
  }
  const discValue = value[discProp];
  if (typeof discValue === 'string') {
    for (const resolved of resolvedVariants) {
      if (readConstProperty(resolved, discProp) === discValue) return resolved;
    }
    // Recognized property but unknown value: return null rather than
    // silently land on an arbitrary variant.
    return null;
  }
  // No discriminator key on the value at all — meaning this value
  // wasn't tagged. Fall through to shape matching: variants that
  // identify themselves by other unique fields (the meta-schema's
  // RefSchema uses `$ref`, UnionSchema uses `oneOf`) are picked by
  // the structure of the value.
  return shapeMatchVariant(resolvedVariants, value);
}

/**
 * Shape-matching variant picker for the walker. Mirrors the logic in
 * build.ts's `valueMatchesShape` but doesn't include the const-property
 * pin-check — for descent purposes, type + required-key matching is
 * enough to disambiguate the meta-schema's RefSchema vs UnionSchema.
 */
function shapeMatchVariant(
  variants: readonly JsonSchema[],
  value: JsonValue | undefined,
): JsonSchema | null {
  if (value === undefined) return null;
  for (const v of variants) {
    if (variantAcceptsValue(v, value)) return v;
  }
  return null;
}

function variantAcceptsValue(schema: JsonSchema, value: JsonValue): boolean {
  // Type match (with same shape inference as normalizeType).
  const t = normalizeType(schema.type, schema);
  if (t !== undefined) {
    const types = Array.isArray(t) ? t : [t];
    let matched = false;
    for (const tt of types) {
      if (tt === 'string' && typeof value === 'string') matched = true;
      else if (tt === 'number' && typeof value === 'number') matched = true;
      else if (tt === 'integer' && Number.isInteger(value)) matched = true;
      else if (tt === 'boolean' && typeof value === 'boolean') matched = true;
      else if (tt === 'null' && value === null) matched = true;
      else if (tt === 'array' && Array.isArray(value)) matched = true;
      else if (
        tt === 'object' &&
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        matched = true;
      }
    }
    if (!matched) return false;
  }
  // Required keys: any required key absent from the value disqualifies the variant.
  if (
    Array.isArray(schema.required) &&
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value)
  ) {
    for (const key of schema.required) {
      if (!(key in value)) return false;
    }
  }
  return true;
}

function normalizeType(
  type: JsonSchema['type'],
  schema: JsonSchema,
): JsonSchema['type'] | undefined {
  if (type === undefined) {
    // Infer from shape when missing — schemas in the wild omit `type` on
    // objects-with-properties constantly.
    if (schema.properties) return 'object';
    if (schema.items) return 'array';
    return undefined;
  }
  if (Array.isArray(type)) {
    const nonNull = type.filter((t) => t !== 'null');
    return nonNull.length === 1 ? nonNull[0] : undefined;
  }
  return type;
}

function pluckObjectValue(value: JsonValue | undefined, key: string): JsonValue | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value[key];
}

function pluckArrayValue(value: JsonValue | undefined, segment: string): JsonValue | undefined {
  if (!Array.isArray(value)) return undefined;
  const n = Number(segment);
  if (!Number.isInteger(n) || n < 0 || n >= value.length) return undefined;
  return value[n];
}
